import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "containers/settings/ui/buttonStyles";
import { btnQuietTextCls } from "components/tw/classes";

/** What `useTranslation().t` is, for helpers that take it as an argument. */
export type Translate = (key: string, options?: any) => string;

const DAY_MS = 24 * 60 * 60 * 1000;

/** A small grey label after a title: another company's name, a kind, a signature type. */
export const CHIP_CLASS = "rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700";

/** Whole days since a moment (ISO). */
export function daysSince(since: string): number {
  return Math.floor((Date.now() - new Date(since).getTime()) / DAY_MS);
}

/** "today" or "3 d. ago" for the moment a task appeared. */
export function taskAge(since: string, t: Translate): string {
  const days = daysSince(since);
  return days < 1 ? t("tasks_age_today") : t("tasks_age_days", { count: days });
}

interface TaskRowProps {
  title: string;
  /** One quiet line under the title. */
  meta?: string;
  /** Small labels after the meta line: another company's name, "Waiting for approval". */
  chips?: string[];
  /** The main button of the row; a row that only informs has none. */
  action?: { label: string; onClick: () => void; quiet?: boolean };
  /** A second, quieter action after the button, e.g. "Sutvarkyta". */
  textAction?: { label: string; onClick: () => void };
  /** A task that waits for someone else: greyed out. */
  muted?: boolean;
}

/** One task of the «Užduotys» page: title, one line of detail, a button and at most one quieter action. */
export default function TaskRow({ title, meta, chips = [], action, textAction, muted = false }: TaskRowProps) {
  return (
    <li
      className={`flex flex-col gap-2 rounded-lg border border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
        muted ? "bg-gray-50" : "bg-white"
      }`}
    >
      <div className="min-w-0">
        <p className={`line-clamp-2 break-words text-sm font-medium ${muted ? "text-gray-600" : "text-gray-900"}`}>
          {title}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
          {meta && <span>{meta}</span>}
          {chips.map((chip) => (
            <span key={chip} className={CHIP_CLASS}>
              {chip}
            </span>
          ))}
        </p>
      </div>
      {(action || textAction) && (
        <div className="flex shrink-0 flex-wrap items-center gap-4">
          {action && (
            <button
              type="button"
              className={action.quiet ? SECONDARY_BUTTON : PRIMARY_BUTTON}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          )}
          {textAction && (
            <button type="button" className={btnQuietTextCls} onClick={textAction.onClick}>
              {textAction.label}
            </button>
          )}
        </div>
      )}
    </li>
  );
}
